// @ts-strict-ignore
import { ArrayUtils } from '@shared/util/array-utils';
import { ProjectModel } from '@project/model/project.model';
import { ArticleManager } from '@article/manager/article.manager';
import { ArticleVO } from '@article/model/vo/article.vo';
import { ArticleUtils } from '@article/util/article-utils';
import { ArticleConstants } from '@article/constants/article-constants';
import { ProjectArticleManager } from '@project/components/article/manager/project-article.manager';
import { UndoRedoManager } from '@shared/undo/undo-redo.manager';
import { ValidationService } from '@project/manager/validation.service';
import { MiniTerminalActuationType, MiniTerminalArticleInfoDef, MiniTerminalMountingType } from './mini-terminal-article-info-def';
import { AlertHelper } from '@shared/alert/alert.helper';
import { ArticlePlausiPropertyDef } from '@article/constants/article-plausi-property-def';
import { ArticleDataService } from '@article/service/article-data.service';
import { ArticleColorVariantsInfoVO } from '@article/model/vo/article-color-variants-info.vo';
import { ArticleContainer } from '@3d-scene/model3d/article-container';
import { MountingRailHelper } from '@project/helper/mounting-rail.helper';
import { Injectable } from '@angular/core';
import { InsertArticleService } from '@project/components/article/service/insert-article.service';
import { lastValueFrom, Observable, Subject } from 'rxjs';
import { Log } from '@shared/debug/log';
import { SelectionModelService } from '@project/components/article/service/selection-model.service';
import { MiniTerminalBlocksArticleManager } from '@article/manager/mini-terminal-blocks-article.manager';
import { QuickFixManager } from '@shared/manager/quick-fix.manager';
import { MiniTerminalBlocksProjectCreationServiceDelegator } from './mini-terminal-blocks-project-creation-service.delegator';
import { MountingRailCreationService } from './mounting-rail-creation.service';
import { MountingRailCreationModel } from './mounting-rail-creation.model';
import { ArticleContainerAttachedService } from '@project/components/article/service/article-container-attached.service';

export interface MiniTerminalBlocksInitialProjectData {
  size: number;
  actuationType: MiniTerminalActuationType;
  mountingType: MiniTerminalMountingType;
  potentialCount: number;
}

export interface MiniTerminalBlocksArticles {
  terminal?: ArticleVO;
  rail?: ArticleVO;
  endPlate?: ArticleVO;
  adapter?: ArticleVO;
  mountingTerminal?: ArticleVO;
}

export interface MiniTerminalBlocksColorVariants {
  terminalArticleNumbers: string[];
  variants: ArticleColorVariantsInfoVO[];
}

@Injectable({ providedIn: 'root' })
export class MiniTerminalBlocksProjectCreationService {
  private readonly TS35_RAIL_OFFSET: number = 300;
  private readonly MAX_ALLOWED_TERMINALS_BETWEEN_SNAP_FOOTS: number = 5;

  private initialProjectData: MiniTerminalBlocksInitialProjectData;
  private articles: MiniTerminalBlocksArticles;
  private articlesToProcess: ArticleVO[];
  private isMountingRailBuildStarted: boolean = false;
  private isProjectBuildStarted: boolean = false;
  private mountingRailCreationModel: MountingRailCreationModel;
  private _projectCreated = new Subject<boolean>();

  constructor(
    miniTerminalBlocksProjectCreationServiceDelegator: MiniTerminalBlocksProjectCreationServiceDelegator,
    private articleManager: ArticleManager,
    private miniTerminalBlocksArticleManager: MiniTerminalBlocksArticleManager,
    private projectModel: ProjectModel,
    private projectArticleManager: ProjectArticleManager,
    private selectionModelService: SelectionModelService,
    private undoRedoManager: UndoRedoManager,
    private validationService: ValidationService,
    private alertHelper: AlertHelper,
    private articleDataService: ArticleDataService,
    private mountingRailHelper: MountingRailHelper,
    private mountingRailCreationService: MountingRailCreationService,
    private _insertArticleService: InsertArticleService,
    private quickFixManager: QuickFixManager,
    private articleContainerAttachedService: ArticleContainerAttachedService,
  ) {
    miniTerminalBlocksProjectCreationServiceDelegator.miniTerminalBlocksProjectCreationService = this;
  }

  public get projectCreated$(): Observable<boolean> {
    return this._projectCreated.asObservable();
  }

  public async getTerminalColorVariants(initialData: MiniTerminalBlocksInitialProjectData): Promise<MiniTerminalBlocksColorVariants> {
    this.initialProjectData = initialData;
    const articleNumbers: string[] = MiniTerminalArticleInfoDef.getArticleNumbers(initialData);
    if (ArrayUtils.isEmpty(articleNumbers)) {
      this.finalizeProjectCreation(false);
      throw new Error('Empty article number array');
    }
    const retrievedArticles: ArticleVO[] = await this.articleManager.loadArticlesByArticleNumbers(articleNumbers);
    if (this.isRetrievedArticlesMissed(articleNumbers, retrievedArticles)) {
      this.finalizeProjectCreation(false);
      throw new Error('Retrieved articles missed');
    }
    this.defineArticles(retrievedArticles);
    this.prepareTerminalsToProcess();
    if (this.isArticlesToProcessMissed()) {
      this.finalizeProjectCreation(false);
      throw new Error('Articles to process missed');
    }
    const terminalArticleNumbers: string[] = this.articlesToProcess.map((article: ArticleVO) => article.articleNo);
    const variants: ArticleColorVariantsInfoVO[] = await lastValueFrom(this.articleDataService
      .getArticleColorVariants(this.getUniqueTerminalArticleNumbers()));
    return { terminalArticleNumbers, variants };
  }

  private isRetrievedArticlesMissed(articleNumbers: string[], articles: ArticleVO[]): boolean {
    let missedArticleNumbers: string[];
    if (articles === null) {
      missedArticleNumbers = articleNumbers;
    } else {
      missedArticleNumbers = articleNumbers.filter((articleNo: string) => {
        return !articles.find((article: ArticleVO) => article && article.articleNo === articleNo);
      });
    }
    if (ArrayUtils.isNotEmpty(missedArticleNumbers)) {
      this.alertHelper.showError('global.title.error', 'createMiniTerminalBlocksDialog.error.message.articles_not_found', {
        translationParams: [missedArticleNumbers.join(', ')],
      });
      return true;
    }
    return false;
  }

  private defineArticles(retrievedArticles: ArticleVO[]): void {
    const articles: MiniTerminalBlocksArticles = {};
    retrievedArticles.forEach((article: ArticleVO) => {
      if (ArticleUtils.isMountingRail(article)) {
        articles.rail = article;
      } else if (ArticleUtils.isEndPlateArticle(article)) {
        articles.endPlate = article;
      } else if (ArticleUtils.isMountingFootArticle(article)) {
        articles.adapter = article;
      } else if (this.isMountingTerminal(article)) {
        articles.mountingTerminal = article;
      } else if (ArticleUtils.isTerminalBlockArticle(article)) {
        articles.terminal = article;
      }
    });
    this.articles = articles;
  }

  private isMountingTerminal(article: ArticleVO): boolean {
    const articleMountingType: string = ArticleUtils.getArticleMountingType(article);
    return (
      (this.initialProjectData.mountingType === MiniTerminalMountingType.FLANGE &&
        articleMountingType === ArticlePlausiPropertyDef.MOUNTING_TYPE_VALUE_FLANGE) ||
      (this.initialProjectData.mountingType === MiniTerminalMountingType.SNAP_FOOT &&
        articleMountingType === ArticlePlausiPropertyDef.MOUNTING_TYPE_VALUE_SNAP_FOOT) ||
      // TODO check condition for TS35 mounting type when building algorithm will be implemented
      (this.initialProjectData.mountingType === MiniTerminalMountingType.TS35 &&
        articleMountingType === ArticlePlausiPropertyDef.MOUNTING_TYPE_VALUE_SNAP_FOOT)
    );
  }

  private prepareTerminalsToProcess(): void {
    this.articlesToProcess = [];
    const potentialCount: number = this.initialProjectData.potentialCount;
    if (this.initialProjectData.mountingType === MiniTerminalMountingType.TS15) {
      for (let i = 1; i <= potentialCount; i++) {
        this.articlesToProcess.push(this.articles.terminal);
      }
    } else if (this.initialProjectData.mountingType === MiniTerminalMountingType.FLANGE) {
      this.articlesToProcess.push(this.articles.mountingTerminal);
      for (let i = 2; i <= potentialCount; i++) {
        this.articlesToProcess.push(this.articles.terminal);
      }
    } else if (this.initialProjectData.mountingType === MiniTerminalMountingType.SNAP_FOOT) {
      this.articlesToProcess.push(this.articles.mountingTerminal);
      const indexPositionsOfSnapFootMountingTerminals: number[] = this.calculateIndexPositionsOfSnapFootMountingTerminals(potentialCount - 2);
      for (let i = 2; i < potentialCount; i++) {
        if (indexPositionsOfSnapFootMountingTerminals.includes(i - 2)) {
          this.articlesToProcess.push(this.articles.mountingTerminal);
        } else {
          this.articlesToProcess.push(this.articles.terminal);
        }
      }
      this.articlesToProcess.push(this.articles.mountingTerminal);
    }
  }

  /**
   * The snap foots (mounting terminals) should as much as possible evenly distributed in the created project,
   * thus we need to calculate the positions to achieve an equal distance between them.
   * So we try to have equal length of sections divided by mounting terminals.
   * @param potentialCount - total number of required potentials (excl. any poles already created, e.g. by start and end mounting terminals)
   */
  private calculateIndexPositionsOfSnapFootMountingTerminals(potentialCount: number): number[] {
    const indexPositionsOfMountingTerminals: number[] = [];
    const amountOfRequiredMountingTerminals: number = Math.ceil(potentialCount / this.MAX_ALLOWED_TERMINALS_BETWEEN_SNAP_FOOTS) - 1;
    if (amountOfRequiredMountingTerminals === 0) {
      return indexPositionsOfMountingTerminals;
    }
    const amountOfRequiredSections: number = amountOfRequiredMountingTerminals + 1;
    const potentialCountOfNonMountingTerminals: number = potentialCount - amountOfRequiredMountingTerminals;
    const smallerAmountOfTerminalsBetweenMountingTerminals: number = Math.floor(potentialCountOfNonMountingTerminals / amountOfRequiredSections);
    const largerAmountOfTerminalsBetweenMountingTerminals: number = Math.ceil(potentialCountOfNonMountingTerminals / amountOfRequiredSections);
    for (let i = 0; i < amountOfRequiredMountingTerminals; i++) {
      let amountOfTerminalsBetweenMountingTerminals: number =
        i % 2 === 0 ? smallerAmountOfTerminalsBetweenMountingTerminals : largerAmountOfTerminalsBetweenMountingTerminals;
      amountOfTerminalsBetweenMountingTerminals = Math.min(amountOfTerminalsBetweenMountingTerminals, this.MAX_ALLOWED_TERMINALS_BETWEEN_SNAP_FOOTS);
      const startPosition: number =
        indexPositionsOfMountingTerminals.length === 0 ? 0 : indexPositionsOfMountingTerminals[indexPositionsOfMountingTerminals.length - 1] + 1;
      indexPositionsOfMountingTerminals.push(startPosition + amountOfTerminalsBetweenMountingTerminals);
    }
    return indexPositionsOfMountingTerminals;
  }

  private isArticlesToProcessMissed(): boolean {
    if (this.articlesToProcess.some((article: ArticleVO) => !article)) {
      this.alertHelper.showError('global.title.error', 'createMiniTerminalBlocksDialog.error.message.articles_could_not_be_identified');
      return true;
    }
    return false;
  }

  private getUniqueTerminalArticleNumbers(): string[] {
    const terminalArticleNumbers: string[] = [];
    this.articlesToProcess.forEach((article: ArticleVO) => {
      if (!terminalArticleNumbers.includes(article.articleNo)) {
        terminalArticleNumbers.push(article.articleNo);
      }
    });
    return terminalArticleNumbers;
  }

  public async createProject(terminalArticleNumbers: string[]): Promise<void> {
    const articleNumbers = [];
    for (const articleNo of terminalArticleNumbers) {
      if (!articleNumbers.includes(articleNo)) {
        articleNumbers.push(articleNo);
      }
    }
    const retrievedArticles: ArticleVO[] = await this.articleManager.loadArticlesByArticleNumbers(articleNumbers);
    this.prepareArticlesToProcess(terminalArticleNumbers, retrievedArticles);
    if (this.isArticlesToProcessMissed()) {
      this.finalizeProjectCreation(false);
    }
    await this.attachMountingRail();
  }

  private prepareArticlesToProcess(terminalArticleNumbers: string[], retrievedArticles: ArticleVO[]): void {
    this.articlesToProcess = [];
    for (const articleNo of terminalArticleNumbers) {
      const article: ArticleVO = retrievedArticles.find((article: ArticleVO) => article.articleNo === articleNo);
      this.articlesToProcess.push(article);
    }
    if (this.initialProjectData.mountingType === MiniTerminalMountingType.FLANGE) {
      this.articlesToProcess.push(this.articles.endPlate);
    }
  }

  private async attachMountingRail(): Promise<void> {
    this.setProjectType();
    const mountingRailArticleId: number = this.articles.rail ? this.articles.rail.identity : ArticleConstants.DEFAULT_FREE_MOUNTING_ARTICLE_ID;
    this.isMountingRailBuildStarted = true;
    this.projectModel.newProjectMountingRail();
    try {
      await this.miniTerminalBlocksArticleManager.quickLoadArticle({
        data: mountingRailArticleId,
        quickLoadType: ArticleManager.QUICK_LOAD_TYPE_LOAD_BY_ARTICLE_ID,
      });
    } catch (error) {
      this.projectCreationFailureHandler();
    }
  }

  private setProjectType(): void {
    const mountingType: MiniTerminalMountingType = this.initialProjectData.mountingType;
    if (mountingType === MiniTerminalMountingType.TS15 || mountingType === MiniTerminalMountingType.TS35) {
      this.projectModel.currentProjectType = "MOUNTING_RAIL";
    } else {
      this.projectModel.currentProjectType = "FREE_MOUNTING";
    }
  }

  public async mountingRailAttached(): Promise<void> {
    if (!this.isMountingRailBuildStarted) {
      return;
    }
    this.isMountingRailBuildStarted = false;
    if (this.initialProjectData.mountingType === MiniTerminalMountingType.TS15) {
      await this.insertLeadingBlockingZone(this.TS35_RAIL_OFFSET);
    } else {
      this.isProjectBuildStarted = true;
      await this.processNextArticle();
    }
  }

  private async insertLeadingBlockingZone(blockingZoneLength: number): Promise<void> {
    const mountingRailContainer: ArticleContainer = this.mountingRailHelper.getMountingRailContainer();
    if (!mountingRailContainer) {
      this.finalizeProjectCreation(false);
    }

    this.projectModel.isMountingRailInProcess = true;
    this.mountingRailCreationModel = new MountingRailCreationModel();
    await this.mountingRailCreationService.setMountingRailArticle(mountingRailContainer.article, this.mountingRailCreationModel)
    this.mountingRailCreationService.setExistingMountingRail(mountingRailContainer, this.mountingRailCreationModel);
    this.mountingRailCreationModel.startBlockingLength = blockingZoneLength;

    await this.withArticleContainerAttachedSubscription(async () => {
      await this.mountingRailCreationService.startLoadMountingRail(mountingRailContainer.article, this.mountingRailCreationModel);

      this.projectModel.isMountingRailInProcess = false;
      this.isProjectBuildStarted = true;
      await this.processNextArticle();
    });
  }

  private async withArticleContainerAttachedSubscription(asyncFunc: () => Promise<void>): Promise<void> {
    const subscription = this.articleContainerAttachedService.attachedArticleContainer$.subscribe({
      next: async (data) => {
        await this.mountingRailCreationService.articleContainerAttached(data, this.mountingRailCreationModel);
      },
      error: error => Log.error('Error in attached article container subscription: ', error),
    });
    try {
      await asyncFunc();
    } finally {
      subscription.unsubscribe();
    }
  }

  public async processNextArticle(): Promise<void> {
    if (!this.isProjectBuildStarted) {
      return;
    }
    if (ArrayUtils.isNotEmpty(this.articlesToProcess)) {
      const articleToProcess: ArticleVO = this.articlesToProcess.shift();
      let articleCount: number = 1;
      while (this.articlesToProcess[0] && this.articlesToProcess[0].identity === articleToProcess.identity) {
        this.articlesToProcess.shift();
        articleCount += 1;
      }
      const addArticleSucceeded = await this.projectArticleManager.addArticles(articleToProcess, articleCount);
      if (addArticleSucceeded) {
        await this.processNextArticle();
      } else {
        this.projectCreationFailureHandler();
      }
    } else {
      await this.projectBuildCompleted();
    }
  }

  private async projectBuildCompleted(): Promise<void> {
    this.isProjectBuildStarted = false;
    if (
      this.initialProjectData.mountingType === MiniTerminalMountingType.TS15
      || this.initialProjectData.mountingType === MiniTerminalMountingType.SNAP_FOOT
    ) {
      await this.validateProject();
    } else {
      this.finalizeProjectCreation();
    }
  }

  private async validateProject(): Promise<void> {
    try {
      const validationResult = await this.validationService.validateProjectAsync();
      await this.quickFixManager.projectValidatedSuccessHandler(validationResult);
    } catch (error) {
      Log.error('Project could not be validated: ', error);
    }
    if (this.initialProjectData.mountingType === MiniTerminalMountingType.TS15) {
      this.projectModel.isMountingRailInProcess = true;
      this.mountingRailCreationService.updateUsedMountingRailLengthData(this.mountingRailCreationModel);
      this.mountingRailCreationModel.mountingRailLength = this.mountingRailCreationModel.usedMountingRailLength + this.TS35_RAIL_OFFSET;
      await this.mountingRailCreationService.startLoadMountingRail(this.mountingRailCreationModel.mountingRailArticle, this.mountingRailCreationModel);

    }
    this.finalizeProjectCreation();
  }

  public projectCreationFailureHandler(): void {
    this.finalizeProjectCreation(false);
  }

  private finalizeProjectCreation(isSuccess: boolean = true): void {
    this.articles = null;
    this.isMountingRailBuildStarted = false;
    this.isProjectBuildStarted = false;
    this.selectionModelService.removeAllSelections();
    this.undoRedoManager.clearAll();
    this.projectModel.isMountingRailInProcess = false;
    if (!isSuccess) {
      this.projectModel.resetProjectChanged();
    }
    this._insertArticleService.markAllArticlesLoaded()
    this._projectCreated.next(isSuccess);
  }
}
